// MUI Dashboard Standard Colors

const neutral = {
  50: "#FAFAFA",
  100: "#F5F5F5",
  200: "#EEEEEE",
  300: "#E0E0E0",
  400: "#BDBDBD",
  500: "#9E9E9E",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#212121",
  1000: "#1A1A1A", // Near black for primary text
  1100: "#F8F9FA", // Extra light for backgrounds
};

const background = {
  default: neutral[50], // #FAFAFA - Very light gray
  paper: "#FFFFFF",
  custom: neutral[100], // #F5F5F5
  custom2: "#FFFFFF",
  custom3: neutral[50],
  custom4: "#FFFFFF",
  footer1: "rgba(0, 0, 0, 0.02)",
  footer2: "rgba(0, 0, 0, 0.04)",
  custom5: neutral[100],
  custom6: "#FFFFFF",
};

const divider = neutral[200]; // #EEEEEE - Subtle divider

const primary = {
  main: "#0D9488", // Teal 600
  deep: "#0F766E", // Teal 700
  light: "#99F6E4", // Teal 200
  dark: "#115E59", // Teal 800
  semiLight: "#CCFBF1", // Teal 100
  contrastText: "#FFFFFF",
  customType1: "#0D9488",
  customType2: "#14B8A6", // Teal 500
  customType3: "#2DD4BF", // Teal 400
  overLay: "rgba(13, 148, 136, 0.8)",
  lite: "rgba(13, 148, 136, 0.1)",
  icon: "#0D9488",
};
const moduleTheme = {
  pharmacy: "#52C41A",
  ecommerce: "#F5F5F5",
  food: "#52C41A",
  parcel: "#52C41A",
};
const horizontalCardBG = "#FFFFFF";

const secondary = {
  main: "#5DADE2",
  light: "#85C1E9",
  dark: "#3498DB",
  contrastText: "#FFFFFF",
};

const success = {
  main: "#10B981", // Modern Green
  light: "#34D399",
  dark: "#059669",
  contrastText: "#FFFFFF",
};

const info = {
  main: "#3B82F6", // Modern Blue
  light: "#60A5FA",
  dark: "#2563EB",
  lite: "#EFF6FF",
  contrastText: "#FFFFFF",
  contrastText1: "#EFF6FF",
  blue: "#3B82F6",
  custom1: "#93C5FD",
};

const warning = {
  main: "#F59E0B", // Modern Amber
  light: "#FCD34D",
  lite: "#FFFBEB",
  liter: "#FFF7ED",
  dark: "#D97706",
  contrastText: "#FFFFFF",
  new: "#FBBF24",
};

const error = {
  main: "#EF4444", // Modern Red
  light: "#F87171",
  dark: "#B91C1C",
  contrastText: "#FFFFFF",
  deepLight: "#FEE2E2",
};

const text = {
  primary: neutral[1000], // #1A1A1A - Near black for primary text
  secondary: neutral[600], // #757575 - Medium gray for secondary text
  disabled: neutral[400], // #BDBDBD - Light gray for disabled text
  hint: neutral[500], // #9E9E9E - Hint text
  custom: "#374151",
  customText1: "#4B5563",
};

const footer = {
  inputButton: "#D1E7ED",
  inputButtonHover: "#2E86AB",
  bottom: "rgba(46, 134, 171, 0.05)",
  foodBottom: "#4B5563",
  appDownloadButtonBg: "#1A4E63",
  appDownloadButtonBgGray: "#5DADE2",
  appDownloadButtonBgHover: "#2E86AB",
  foodFooterBg: "#1E5F7A",
};
const customColor = {
  textGray: "#9CA3AF",
  textGrayDeep: "#4B5563",
  buyButton: "#D1E7ED",
};
const whiteContainer = {
  main: "#FFFFFF",
};
const pink = {
  main: "#EC4899",
};
const foodCardColor = "#FFFFFF";
const paperBoxShadow = "rgba(145, 158, 171, 0.08)";
const roundStackOne = "rgba(255, 255, 255, 0.1)";
const roundStackTwo = "rgba(255, 255, 255, 0.15)";
const toolTipColor = neutral[900];

export const lightThemeOptions = {
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[200],
          color: text.primary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          "&.MuiChip-filledDefault": {
            backgroundColor: neutral[200],
            "& .MuiChip-deleteIcon": {
              color: neutral[500],
            },
          },
          "&.MuiChip-outlinedDefault": {
            borderColor: neutral[300],
            "& .MuiChip-deleteIcon": {
              color: neutral[400],
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            opacity: 1,
            color: text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: neutral[300],
        },
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #fff inset",
            WebkitTextFillColor: text.primary,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: "solid",
          borderWidth: 0,
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: "solid",
          borderWidth: 0,
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: neutral[500],
        },
        track: {
          backgroundColor: neutral[400],
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${divider}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[100],
          ".MuiTableCell-root": {
            color: text.secondary,
          },
        },
      },
    },
  },
  palette: {
    action: {
      active: neutral[600],
      focus: "rgba(46, 134, 171, 0.08)",
      hover: "rgba(46, 134, 171, 0.04)",
      selected: "rgba(46, 134, 171, 0.08)",
      disabledBackground: "rgba(145, 158, 171, 0.12)",
      disabled: "rgba(145, 158, 171, 0.48)",
    },
    horizontalCardBG,
    background,
    divider,
    error,
    info,
    mode: "light",
    neutral,
    primary,
    secondary,
    success,
    text,
    warning,
    footer,
    customColor,
    whiteContainer,
    pink,
    paperBoxShadow,
    foodCardColor,
    moduleTheme,
    roundStackOne,
    roundStackTwo,
    toolTipColor,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(145, 158, 171, 0.08)", // 1
    "0px 4px 8px rgba(145, 158, 171, 0.08)", // 2
    "0px 8px 16px rgba(145, 158, 171, 0.08)", // 3
    "0px 12px 24px rgba(145, 158, 171, 0.08)", // 4
    "0px 16px 32px rgba(145, 158, 171, 0.08)", // 5
    "0px 20px 40px rgba(145, 158, 171, 0.08)", // 6
    "0px 24px 48px rgba(145, 158, 171, 0.08)", // 7
    "0px 4px 12px rgba(13, 148, 136, 0.16)", // 8 - Teal colored shadow
    "0px 8px 24px rgba(13, 148, 136, 0.16)", // 9
    "0px 12px 32px rgba(13, 148, 136, 0.16)", // 10
    "0px 16px 48px rgba(13, 148, 136, 0.16)", // 11
    "0px 24px 48px rgba(13, 148, 136, 0.24)", // 12
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 13
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 14
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 15
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 16
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 17
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 18
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 19
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 20
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 21
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 22
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 23
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)", // 24
  ],
};
